/**
 * Free Parties Page - "5 free parties" community giveaway (owner request
 * 2026-09-12). Entries are collected in the owner's Google Form; this page
 * is the shareable link for social posts plus the details and fine print.
 * After the deadline the form button is replaced with a closed notice so
 * the page never invites entries we won't read.
 */

import React from 'react';

import { Page, LayoutSingleColumn, NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import { FREE_PARTIES_FORM_URL, isFreePartiesOpen } from './freePartiesPromo';
import css from './FreePartiesPage.module.css';

const STEPS = [
  {
    heading: 'Tell us about your party',
    body:
      "Fill out a short form about the party you'd love to throw, or nominate someone who deserves one.",
  },
  {
    heading: 'We read every entry',
    body:
      'Birthdays, showers, reunions, a thank-you for your block. Every kind of celebration counts.',
  },
  {
    heading: 'Five parties, on us',
    body: 'We pick five winners, reach out, and plan the party together.',
  },
];

const FINE_PRINT = [
  'No purchase or Merrily account needed to enter.',
  'The party must take place in the Carpinteria to Goleta area: Carpinteria, Summerland, Montecito, Santa Barbara, or Goleta.',
  "Anyone can enter or nominate someone, including kids. Entrants under 18 should get a parent or guardian's help with the form.",
  'Entries close October 12, 2026 at 11:59 pm Pacific.',
  'The Merrily team picks five winners based on the entries.',
  'Each prize is a complete party for up to 50 guests, as described above. What it includes depends on the party. Venue coverage is limited to a park or public space reservation.',
  "Prizes can't be exchanged for cash or transferred.",
  "With winners' permission, we may share photos and stories from the parties.",
];

export const FreePartiesPageComponent = () => {
  const isOpen = isFreePartiesOpen();

  // Brand suffix appended centrally in Page.js (single + consistent).
  const title = 'Win a Free Party in Santa Barbara';
  const description =
    'Merrily is giving away 5 complete parties in Santa Barbara County: rentals, catering, florals, music and more. Enter by October 12, 2026.';

  const cta = isOpen ? (
    <a
      className={css.ctaButton}
      href={FREE_PARTIES_FORM_URL}
      target="_blank"
      rel="noopener noreferrer"
    >
      Enter or nominate someone
    </a>
  ) : (
    <p className={css.closedNotice}>
      Entries are closed. Thank you to everyone who shared a party idea!
    </p>
  );

  return (
    <Page className={css.root} title={title} description={description} scrollingDisabled={false}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.main}>
          <article className={css.container}>
            <p className={css.eyebrow}>Santa Barbara County giveaway</p>
            <h1 className={css.title}>We're throwing 5 free parties</h1>
            <p className={css.intro}>
              Merrily is all about neighbors helping neighbors celebrate. To say hello, we're giving
              away five complete parties in Santa Barbara County. Tell us about the party you'd love
              to throw, or nominate someone who deserves one.
            </p>

            <div className={css.ctaBlock}>
              {cta}
              {isOpen ? <p className={css.deadline}>Enter by October 12, 2026</p> : null}
            </div>

            <section className={css.section}>
              <h2 className={css.sectionHeading}>What winners get</h2>
              <p className={css.body}>
                A complete party for up to 50 guests, planned around your celebration. Depending on
                the party, that can include rentals, catering, florals, music and more.
              </p>
              <p className={css.body}>
                Need a place to host? We'll cover a park or public space reservation. Private event
                venues aren't included.
              </p>
            </section>

            <section className={css.section}>
              <h2 className={css.sectionHeading}>How it works</h2>
              <ol className={css.steps}>
                {STEPS.map((s, i) => (
                  <li key={s.heading} className={css.step}>
                    <span className={css.stepNumber} aria-hidden="true">
                      {i + 1}
                    </span>
                    <div>
                      <h3 className={css.stepHeading}>{s.heading}</h3>
                      <p className={css.body}>{s.body}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            <section className={css.section}>
              <h2 className={css.sectionHeading}>Who can enter</h2>
              <p className={css.body}>
                Anyone, kids included! The party just needs to happen in the Carpinteria to Goleta
                area: Carpinteria, Summerland, Montecito, Santa Barbara, or Goleta.
              </p>
            </section>

            <div className={css.ctaBlock}>{cta}</div>

            <section className={css.finePrint}>
              <h2 className={css.finePrintHeading}>The fine print</h2>
              <ul className={css.finePrintList}>
                {FINE_PRINT.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <p className={css.finePrintNote}>
                Can't wait for a free party?{' '}
                <NamedLink name="SearchPage" className={css.inlineLink}>
                  Browse party kits near you
                </NamedLink>
                .
              </p>
            </section>
          </article>
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

export default FreePartiesPageComponent;
